import get from 'lodash/get';
import isNil from 'lodash/isNil';

import { type IServerSideGetRowsRequest, type SortModelItem } from 'ag-grid-community';

import { type ViewColumn } from 'common/types/viewColumn';

import { type DataValue, type InlineDataRows, type OrderConfig, type Vif } from '../vif';
import { dimensionColumnPath, inlineRowsPath } from './AgTableObjectPathHelpers';

type AgInlineRow = { [fieldName: string]: DataValue };

// AG Grid's own comparator treats null as the smallest value, which we match.
const compareCellValues = (a: DataValue, b: DataValue): number => {
  if (a === b) {
    return 0;
  }
  if (isNil(a)) {
    return -1;
  }
  if (isNil(b)) {
    return 1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return String(a).localeCompare(String(b));
};

const sortRows = (rows: AgInlineRow[], sortModel: SortModelItem[]): AgInlineRow[] => {
  if (sortModel.length === 0) {
    return rows;
  }

  return [...rows].sort((rowA, rowB) => {
    for (const { colId, sort } of sortModel) {
      const comparison = compareCellValues(rowA[colId], rowB[colId]);

      if (comparison !== 0) {
        return sort === 'desc' ? -comparison : comparison;
      }
    }

    return 0;
  });
};

const toRowObjects = (rows: InlineDataRows, columns: ViewColumn[]): AgInlineRow[] =>
  rows.map((row) =>
    Object.fromEntries(columns.map((column, index) => [column.fieldName, row[index]])),
  ) as AgInlineRow[];

const orderConfigToSortModel = (order: OrderConfig[]): SortModelItem[] =>
  order.map(({ columnName, ascending }) => ({ colId: columnName, sort: ascending ? 'asc' : 'desc' }));

/**
 * Shapes a `socrata.inline` VIF's rows for AG Grid's server side row model. The rows are
 * positional arrays lining up with the dimension columns, and there is no query to push sorting
 * or paging to, so both happen here.
 */
export function setAgInlineDataQuery({
  vifForDataQuery,
  startIndex,
  pageSize,
  pagingEnabled,
  order,
  agRequest,
}: {
  vifForDataQuery: Vif;
  startIndex: number;
  pageSize: number;
  pagingEnabled: boolean;
  order?: OrderConfig[];
  agRequest?: IServerSideGetRowsRequest;
}) {
  const columns: ViewColumn[] = get(vifForDataQuery, dimensionColumnPath, []);
  const rows = toRowObjects(get(vifForDataQuery, inlineRowsPath, []), columns);
  const sortModel = order?.length ? orderConfigToSortModel(order) : (agRequest?.sortModel ?? []);
  const sortedRows = sortRows(rows, sortModel);

  return Promise.resolve({
    datasetRowCount: sortedRows.length,
    rows: pagingEnabled ? sortedRows.slice(startIndex, startIndex + pageSize) : sortedRows,
    vif: vifForDataQuery,
  });
}
